import React, { createContext, useContext, useState, useEffect } from 'react'

export type UserRole = 'visitor' | 'vendor' | 'staff' | 'admin'

export interface UserProfile {
  id: string
  name: string
  role: UserRole
  avatar?: string
  isDetected?: boolean
}

interface UserRoleContextType {
  currentUser: UserProfile | null
  setCurrentUser: (user: UserProfile | null) => void
  userRole: UserRole
  setUserRole: (role: UserRole) => void
  isRoleDetected: boolean
  setIsRoleDetected: (detected: boolean) => void
  availableRoles: Array<{
    value: UserRole
    label: string
    description: string
    icon: string
  }>
}

const UserRoleContext = createContext<UserRoleContextType | undefined>(undefined)

export function useUserRole() {
  const context = useContext(UserRoleContext)
  if (!context) {
    throw new Error('useUserRole must be used within a UserRoleProvider')
  }
  return context
}

interface UserRoleProviderProps {
  children: React.ReactNode
}

export function UserRoleProvider({ children }: UserRoleProviderProps) {
  const defaultUser: UserProfile = {
    id: 'butler-beta-user',
    name: 'Sean Butler',
    role: 'admin',
    isDetected: true,
  }

  const [currentUser, setCurrentUser] = useState<UserProfile | null>(defaultUser)
  const [userRole, setUserRole] = useState<UserRole>('admin')
  const [isRoleDetected, setIsRoleDetected] = useState(true)

  const availableRoles = [
    {
      value: 'admin' as UserRole,
      label: 'Butler Ops',
      description: 'Authenticated Butler beta operator',
      icon: '🎡'
    }
  ]

  useEffect(() => {
    const savedRole = localStorage.getItem('userRole') as UserRole
    const savedUser = localStorage.getItem('currentUser')
    const savedDetection = localStorage.getItem('isRoleDetected')
    
    if (savedRole === 'admin') {
      setUserRole(savedRole)
    }
    
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser))
      } catch (error) {
        console.error('Failed to parse saved user:', error)
      }
    }
    
    if (savedDetection) {
      setIsRoleDetected(savedDetection === 'true')
    }
  }, [])

  // Save role to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('userRole', userRole)
  }, [userRole])

  // Save user to localStorage when it changes
  useEffect(() => {
    if (currentUser) {
      localStorage.setItem('currentUser', JSON.stringify(currentUser))
    } else {
      localStorage.removeItem('currentUser')
    }
  }, [currentUser])

  // Save detection status to localStorage
  useEffect(() => {
    localStorage.setItem('isRoleDetected', isRoleDetected.toString())
  }, [isRoleDetected])

  const handleSetCurrentUser = (user: UserProfile | null) => {
    const nextUser = user ?? defaultUser
    setCurrentUser(nextUser)
    setUserRole('admin')
    setIsRoleDetected(true)
  }

  return (
    <UserRoleContext.Provider value={{
      currentUser,
      setCurrentUser: handleSetCurrentUser,
      userRole,
      setUserRole,
      isRoleDetected,
      setIsRoleDetected,
      availableRoles
    }}>
      {children}
    </UserRoleContext.Provider>
  )
}
